/**
 * Default templates for plugin code
 * These are used as starter templates when creating new plugins
 *
 * SYNC REQUIREMENT: These templates must use the correct API method names that match
 * the BoundPluginApi interface in apps/website/src/@fuse/api/pluginDataApi.ts
 *
 * Current API methods available:
 * - getPluginConfig() / savePluginConfig(config)
 * - getPluginEventInstanceData() / savePluginEventInstanceData(data)
 * - getEventPluginUserData() / saveEventPluginUserData(data)
 */
import { UserRole } from "@fuse/recoil/pluginStudio/types"

export const defaultPluginTemplates = {
  [UserRole.GUEST]: `import React, { useState, useEffect } from 'react';

export default function GuestView({ pluginApi, pluginComponents, pluginStyles }) {
  const { 
    TextArea, 
    Button, 
    AppButton,
    EventHeader,
    ContentPanel,
    TextInput,
    Spinner,
    Alert
  } = pluginComponents || {};
  
  // Safely access styles with fallbacks
  const styles = {
    container: pluginStyles?.layout?.container || 'p-4',
    utils: {
      center: pluginStyles?.utils?.center || 'flex items-center justify-center',
    },
    text: {
      small: pluginStyles?.text?.small || 'text-sm text-white/70',
    }
  };

  const [personalNotes, setPersonalNotes] = useState('');
  const [activityFeed, setActivityFeed] = useState([]);
  const [publicNote, setPublicNote] = useState('');
  const [config, setConfig] = useState({ 
    allowPersonalNotes: true, 
    showActivityFeed: true,
    allowGuestsToPost: true,
    feedTitle: 'Activity Feed',
    personalNotesTitle: 'Personal Notes'
  });
  const [loading, setLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');

  // Load data on component mount
  useEffect(() => {
    async function loadData() {
      try {
        // Load plugin configuration
        const configData = await pluginApi.getPluginConfig();
        if (configData) {
          setConfig(prevConfig => ({...prevConfig, ...configData}));
        }
        
        // Load user's personal notes from user plugin data
        const userData = await pluginApi.getEventPluginUserData();
        if (userData && userData.personalNotes) {
          setPersonalNotes(userData.personalNotes);
        }
        
        // Load event activity feed from event instance data
        const eventData = await pluginApi.getPluginEventInstanceData();
        if (eventData && eventData.activityFeed) {
          setActivityFeed(eventData.activityFeed);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load data. Please try refreshing.');
      } finally {
        setLoading(false);
      }
    }
    
    loadData();
  }, []);

  // Save personal notes
  const savePersonalNotes = async () => {
    try {
      await pluginApi.saveEventPluginUserData({ personalNotes });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving personal notes:', error);
      setError('Failed to save notes. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Add note to activity feed
  const addToActivityFeed = async () => {
    if (!publicNote.trim()) return;
    
    // Check if guests are allowed to post
    if (!config.allowGuestsToPost) {
      setError('Guests are not allowed to post to the activity feed.');
      setTimeout(() => setError(''), 3000);
      return;
    }
    
    try {
      const newEntry = {
        id: Date.now().toString(),
        text: publicNote,
        timestamp: new Date().toISOString(),
        userId: 'current-user', // In a real app, this would be the actual user ID
      };
      
      const updatedFeed = [...activityFeed, newEntry];
      
      // Save to event instance data
      await pluginApi.savePluginEventInstanceData({ activityFeed: updatedFeed });
      
      // Update local state
      setActivityFeed(updatedFeed);
      setPublicNote('');
    } catch (error) {
      console.error('Error adding to activity feed:', error);
      setError('Failed to add note to activity feed. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  if (loading) {
    return (
      <div className={styles.utils.center} style={{height: "200px"}}>
        {Spinner ? <Spinner size="large" /> : <div>Loading...</div>}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {error && Alert && <Alert type="error" onClose={() => setError('')}>{error}</Alert>}
      {saveSuccess && Alert && <Alert type="success" onClose={() => setSaveSuccess(false)}>Your notes have been saved!</Alert>}
      
      {/* Header Section */}
      {EventHeader ? (
        <EventHeader title="About This Plugin">
          This plugin allows you to take personal notes and share thoughts with other attendees.
          Take part in the conversation and engage with others at this event!
        </EventHeader>
      ) : (
        <div className="bg-neutral-800/80 rounded-2xl p-5 mb-4 text-white">
          <h2 className="text-2xl font-medium mb-3">About This Plugin</h2>
          <div className="text-white/90 text-lg">
            This plugin allows you to take personal notes and share thoughts with other attendees.
            Take part in the conversation and engage with others at this event!
          </div>
        </div>
      )}
      
      {/* Personal Notes Section */}
      {config.allowPersonalNotes && (
        ContentPanel ? (
          <ContentPanel title={config.personalNotesTitle || "Personal Notes"}>
            <p className={styles.text.small}>These notes are private and only visible to you.</p>
            
            <TextArea
              value={personalNotes}
              onChange={setPersonalNotes}
              placeholder="Write your personal notes here..."
              rows={5}
              className="mb-4"
            />
            
            {AppButton ? (
              <AppButton 
                onClick={savePersonalNotes}
              >
                Save Notes
              </AppButton>
            ) : (
              <button 
                onClick={savePersonalNotes}
                className="w-full py-4 px-6 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-white text-center font-medium"
              >
                Save Notes
              </button>
            )}
          </ContentPanel>
        ) : (
          <div className="mb-6 bg-neutral-800/80 border border-white/10 rounded-2xl p-5">
            <h2 className="text-xl font-semibold text-white mb-2">
              {config.personalNotesTitle || "Personal Notes"}
            </h2>
            <p className={styles.text.small}>These notes are private and only visible to you.</p>
            
            <TextArea
              value={personalNotes}
              onChange={setPersonalNotes}
              placeholder="Write your personal notes here..."
              rows={5}
              className="mb-4"
            />
            
            <button 
              onClick={savePersonalNotes}
              className="w-full py-4 px-6 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-white text-center font-medium"
            >
              Save Notes
            </button>
          </div>
        )
      )}

      {/* Activity Feed Section */}
      {config.showActivityFeed && (
        ContentPanel ? (
          <ContentPanel title={config.feedTitle || "Activity Feed"}>
            <p className={styles.text.small}>Share notes with everyone attending this event.</p>
            
            {/* Add new note */}
            {config.allowGuestsToPost && (
              <div className="mb-4">
                <TextArea
                  value={publicNote}
                  onChange={setPublicNote}
                  placeholder="Write a note to share with everyone..."
                  rows={3}
                  className="mb-2"
                />
                
                {AppButton ? (
                  <AppButton 
                    onClick={addToActivityFeed}
                  >
                    Post to Activity Feed
                  </AppButton>
                ) : (
                  <button
                    onClick={addToActivityFeed}
                    className="w-full py-4 px-6 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-white text-center font-medium"
                  >
                    Post to Activity Feed
                  </button>
                )}
              </div>
            )}
            
            {/* Activity feed entries */}
            <div className="mt-4 space-y-3">
              {activityFeed.length === 0 ? (
                <p className={styles.text.small + " italic"}>No activity yet. Be the first to post!</p>
              ) : (
                activityFeed.map(entry => (
                  <div key={entry.id} className="p-4 bg-white/5 rounded-xl border border-white/10">
                    <p className="text-white">{entry.text}</p>
                    <p className="text-xs text-white/60 mt-1">
                      {new Date(entry.timestamp).toLocaleString()}
                    </p>
                  </div>
                ))
              )}
            </div>
          </ContentPanel>
        ) : (
          <div className="mb-6 bg-neutral-800/80 border border-white/10 rounded-2xl p-5">
            <h2 className="text-xl font-semibold text-white mb-2">
              {config.feedTitle || "Activity Feed"}
            </h2>
            <p className={styles.text.small}>Share notes with everyone attending this event.</p>
            
            {config.allowGuestsToPost && (
              <div className="mb-4">
                <TextArea
                  value={publicNote}
                  onChange={setPublicNote}
                  placeholder="Write a note to share with everyone..."
                  rows={3}
                  className="mb-2"
                />
                
                <button
                  onClick={addToActivityFeed}
                  className="w-full py-4 px-6 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-white text-center font-medium"
                >
                  Post to Activity Feed
                </button>
              </div>
            )}
            
            <div className="mt-4 space-y-3">
              {activityFeed.length === 0 ? (
                <p className={styles.text.small + " italic"}>No activity yet. Be the first to post!</p>
              ) : (
                activityFeed.map(entry => (
                  <div key={entry.id} className="p-4 bg-white/5 rounded-xl border border-white/10">
                    <p className="text-white">{entry.text}</p>
                    <p className="text-xs text-white/60 mt-1">
                      {new Date(entry.timestamp).toLocaleString()}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        )
      )}
      
      <div className={styles.text.small + " mt-4"}>
        <p>User Role: Guest</p>
      </div>
    </div>
  );
}`,

  [UserRole.HOST_EVENT_VIEW]: `import React, { useState, useEffect } from 'react';

export default function HostEventView({ pluginApi, pluginComponents, pluginStyles }) {
  // Safely access components with fallbacks
  const { 
    TextArea, 
    Button,
    AppButton,
    EventHeader,
    ContentPanel, 
    TextInput,
    Checkbox,
    Spinner,
    Alert
  } = pluginComponents || {};
  
  // Safely access styles with fallbacks
  const styles = {
    container: pluginStyles?.layout?.container || 'p-4',
    utils: {
      center: pluginStyles?.utils?.center || 'flex items-center justify-center',
    },
    text: {
      small: pluginStyles?.text?.small || 'text-sm text-white/70',
      subtitle: pluginStyles?.text?.subtitle || 'text-xl font-semibold text-white mb-3',
      title: pluginStyles?.text?.title || 'text-2xl font-bold text-white mb-4',
    }
  };

  // Plugin configuration state - still needed for display options
  const [config, setConfig] = useState({
    allowPersonalNotes: true,
    showActivityFeed: true,
    allowGuestsToPost: true,
    moderateBeforePost: false,
    feedTitle: 'Activity Feed',
    personalNotesTitle: 'Personal Notes',
  });

  // UI state
  const [loading, setLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');
  const [activityFeed, setActivityFeed] = useState([]);
  const [personalNotes, setPersonalNotes] = useState('');

  // Load plugin data on mount
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        
        // Load plugin configuration
        const configData = await pluginApi.getPluginConfig();
        if (configData && Object.keys(configData).length > 0) {
          setConfig(prevConfig => ({...prevConfig, ...configData}));
        }
        
        // Load activity feed data from event instance data
        const eventData = await pluginApi.getPluginEventInstanceData();
        if (eventData && eventData.activityFeed) {
          setActivityFeed(eventData.activityFeed);
        }
        
        // Load personal notes from user plugin data
        const userData = await pluginApi.getEventPluginUserData();
        if (userData && userData.personalNotes) {
          setPersonalNotes(userData.personalNotes);
        }
      } catch (err) {
        console.error('Error loading plugin data:', err);
        setError('Failed to load plugin data. Please try refreshing.');
      } finally {
        setLoading(false);
      }
    }
    
    loadData();
  }, []);

  // Save personal notes
  const savePersonalNotes = async () => {
    try {
      await pluginApi.saveEventPluginUserData({ personalNotes });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving notes:', err);
      setError('Failed to save notes. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Delete activity feed entry
  const deleteActivityEntry = async (entryId) => {
    try {
      const updatedFeed = activityFeed.filter(entry => entry.id !== entryId);
      await pluginApi.savePluginEventInstanceData({ activityFeed: updatedFeed });
      setActivityFeed(updatedFeed);
    } catch (err) {
      console.error('Error deleting entry:', err);
      setError('Failed to delete entry. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  if (loading) {
    return (
      <div className={styles.utils.center} style={{height: "200px"}}>
        {Spinner ? <Spinner size="large" /> : <div>Loading...</div>}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {error && Alert && <Alert type="error" onClose={() => setError('')}>{error}</Alert>}
      {saveSuccess && Alert && <Alert type="success" onClose={() => setSaveSuccess(false)}>Your changes have been saved!</Alert>}
      
      {/* Header Section */}
      {EventHeader ? (
        <EventHeader title="Host Dashboard">
          Welcome to the host view. Here you can manage your personal notes and the activity feed.
        </EventHeader>
      ) : (
        <div className="bg-neutral-800/80 rounded-2xl p-5 mb-4 text-white">
          <h2 className="text-2xl font-medium mb-3">Host Dashboard</h2>
          <div className="text-white/90 text-lg">
            Welcome to the host view. Here you can manage your personal notes and the activity feed.
          </div>
        </div>
      )}
      
      {/* Personal Notes Section */}
      {config.allowPersonalNotes && (
        ContentPanel ? (
          <ContentPanel title={config.personalNotesTitle}>
            <p className={styles.text.small}>These notes are only visible to you.</p>
            
            <TextArea
              value={personalNotes}
              onChange={setPersonalNotes}
              placeholder="Write your personal notes here..."
              rows={4}
              className="mb-4"
            />
            
            {AppButton ? (
              <AppButton onClick={savePersonalNotes}>
                Save Notes
              </AppButton>
            ) : (
              <button
                onClick={savePersonalNotes}
                className="w-full py-4 px-6 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-white text-center font-medium"
              >
                Save Notes
              </button>
            )}
          </ContentPanel>
        ) : (
          <div className="mb-6 bg-neutral-800/80 border border-white/10 rounded-2xl p-5">
            <h2 className="text-xl font-semibold text-white mb-2">
              {config.personalNotesTitle}
            </h2>
            <p className={styles.text.small}>These notes are only visible to you.</p>
            
            <TextArea
              value={personalNotes}
              onChange={setPersonalNotes}
              placeholder="Write your personal notes here..."
              rows={4}
              className="mb-4"
            />
            
            <button
              onClick={savePersonalNotes}
              className="w-full py-4 px-6 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-white text-center font-medium"
            >
              Save Notes
            </button>
          </div>
        )
      )}
      
      {/* Activity Feed Management */}
      {config.showActivityFeed && (
        ContentPanel ? (
          <ContentPanel title={config.feedTitle + " Management"}>
            <p className={styles.text.small}>Manage posts from all attendees.</p>
            
            <div className="mt-4 space-y-3">
              {activityFeed.length === 0 ? (
                <p className={styles.text.small + " italic"}>No activity yet.</p>
              ) : (
                activityFeed.map(entry => (
                  <div key={entry.id} className="p-4 bg-white/5 rounded-xl border border-white/10 flex justify-between items-center">
                    <div>
                      <p className="text-white">{entry.text}</p>
                      <p className="text-xs text-white/60 mt-1">
                        {new Date(entry.timestamp).toLocaleString()}
                      </p>
                    </div>
                    
                    <button
                      onClick={() => deleteActivityEntry(entry.id)}
                      className="bg-red-500 hover:bg-red-600 text-white text-sm py-1 px-3 rounded"
                    >
                      Delete
                    </button>
                  </div>
                ))
              )}
            </div>
          </ContentPanel>
        ) : (
          <div className="mb-6 bg-neutral-800/80 border border-white/10 rounded-2xl p-5">
            <h2 className="text-xl font-semibold text-white mb-2">
              {config.feedTitle} Management
            </h2>
            <p className={styles.text.small}>Manage posts from all attendees.</p>
            
            <div className="mt-4 space-y-3">
              {activityFeed.length === 0 ? (
                <p className={styles.text.small + " italic"}>No activity yet.</p>
              ) : (
                activityFeed.map(entry => (
                  <div key={entry.id} className="p-4 bg-white/5 rounded-xl border border-white/10 flex justify-between items-center">
                    <div>
                      <p className="text-white">{entry.text}</p>
                      <p className="text-xs text-white/60 mt-1">
                        {new Date(entry.timestamp).toLocaleString()}
                      </p>
                    </div>
                    
                    <button
                      onClick={() => deleteActivityEntry(entry.id)}
                      className="bg-red-500 hover:bg-red-600 text-white text-sm py-1 px-3 rounded"
                    >
                      Delete
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )
      )}
      
      <div className={styles.text.small + " mt-4"}>
        <p>User Role: Host Event View</p>
      </div>
    </div>
  );
}`,

  [UserRole.HOST_CONFIG_VIEW]: `import React, { useState, useEffect } from 'react';

export default function HostConfigView({ pluginApi, pluginComponents, pluginStyles }) {
  // Safely access components with fallbacks
  const { 
    EventHeader, 
    ContentPanel, 
    AppButton, 
    TextInput, 
    TextArea,
    Checkbox, 
    Alert,
    Spinner
  } = pluginComponents || {};
  
  // Safely access styles with fallbacks
  const styles = {
    container: pluginStyles?.layout?.container || 'p-4',
    text: {
      title: pluginStyles?.text?.title || 'text-2xl font-bold text-white mb-4',
      small: pluginStyles?.text?.small || 'text-sm text-white/70',
    }
  };

  // State for plugin configuration
  const [config, setConfig] = useState({
    allowPersonalNotes: true,
    showActivityFeed: true,
    allowGuestsToPost: true,
    moderateBeforePost: false,
    feedTitle: 'Activity Feed',
    personalNotesTitle: 'Personal Notes',
    welcomeMessage: 'Welcome to the event plugin! Use this space to take notes and interact with other attendees.',
  });

  // UI state
  const [loading, setLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  // Load plugin configuration
  useEffect(() => {
    async function loadConfig() {
      try {
        setLoading(true);
        
        // Load plugin configuration
        const configData = await pluginApi.getPluginConfig();
        if (configData) {
          setConfig(prevConfig => ({...prevConfig, ...configData}));
        }
      } catch (err) {
        console.error('Error loading plugin configuration:', err);
        setError('Failed to load plugin configuration. Please try refreshing.');
      } finally {
        setLoading(false);
      }
    }
    
    loadConfig();
  }, []);

  // Handle configuration changes
  const handleConfigChange = (field, value) => {
    setConfig({
      ...config,
      [field]: value
    });
  };

  // Save plugin configuration
  const saveConfig = async () => {
    try {
      await pluginApi.savePluginConfig(config);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving plugin configuration:', err);
      setError('Failed to save plugin configuration. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Reset to factory defaults
  const resetToFactoryDefaults = async () => {
    try {
      setIsResetting(true);
      
      const factoryDefaults = {
        allowPersonalNotes: true,
        showActivityFeed: true,
        allowGuestsToPost: true,
        moderateBeforePost: false,
        feedTitle: 'Activity Feed',
        personalNotesTitle: 'Personal Notes',
        welcomeMessage: 'Welcome to the event plugin! Use this space to take notes and interact with other attendees.',
      };
      
      await pluginApi.savePluginConfig(factoryDefaults);
      setConfig(factoryDefaults);
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error resetting to factory defaults:', err);
      setError('Failed to reset to factory defaults. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsResetting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        {Spinner ? <Spinner size="large" /> : <div>Loading...</div>}
      </div>
    );
  }
  
  return (
    <div className={styles.container}>
      {error && Alert && <Alert type="error" onClose={() => setError('')}>{error}</Alert>}
      {saveSuccess && Alert && <Alert type="success" onClose={() => setSaveSuccess(false)}>Your changes have been saved!</Alert>}
      
      {EventHeader ? (
        <EventHeader title="Plugin Configuration">
          This is the admin view of the plugin. Here you can configure the settings for this plugin instance.
        </EventHeader>
      ) : (
        <div className="bg-neutral-800/80 rounded-2xl p-5 mb-4 text-white">
          <h2 className="text-2xl font-medium mb-3">Plugin Configuration</h2>
          <div className="text-white/90 text-lg">
            This is the admin view of the plugin. Here you can configure the settings for this plugin instance.
          </div>
        </div>
      )}
      
      {ContentPanel ? (
        <ContentPanel title="Plugin Configuration">
          <p className={styles.text.small + " mb-4"}>
            Configure how this plugin behaves for all users in this event.
          </p>
          
          <div className="space-y-4 mb-4">
            {TextInput ? (
              <>
                <TextInput
                  label="Personal Notes Title"
                  value={config.personalNotesTitle}
                  onChange={(value) => handleConfigChange('personalNotesTitle', value)}
                  placeholder="Personal Notes"
                />
                
                <TextInput
                  label="Activity Feed Title"
                  value={config.feedTitle}
                  onChange={(value) => handleConfigChange('feedTitle', value)}
                  placeholder="Activity Feed"
                />
              </>
            ) : (
              <>
                <div className="mb-4">
                  <label className="block text-white mb-1">Personal Notes Title</label>
                  <input
                    type="text"
                    value={config.personalNotesTitle}
                    onChange={(e) => handleConfigChange('personalNotesTitle', e.target.value)}
                    placeholder="Personal Notes"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-white mb-1">Activity Feed Title</label>
                  <input
                    type="text"
                    value={config.feedTitle}
                    onChange={(e) => handleConfigChange('feedTitle', e.target.value)}
                    placeholder="Activity Feed"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </>
            )}
            
            {/* Welcome message */}
            <div className="mb-4">
              <label className="block text-white mb-1">Welcome Message</label>
              <TextArea
                value={config.welcomeMessage}
                onChange={(value) => handleConfigChange('welcomeMessage', value)}
                placeholder="Welcome message for plugin users..."
                rows={3}
                className="mb-2"
              />
            </div>
            
            {Checkbox ? (
              <>
                <Checkbox
                  label="Allow personal notes"
                  checked={config.allowPersonalNotes}
                  onChange={(value) => handleConfigChange('allowPersonalNotes', value)}
                />
                
                <Checkbox
                  label="Show activity feed"
                  checked={config.showActivityFeed}
                  onChange={(value) => handleConfigChange('showActivityFeed', value)}
                />
                
                <Checkbox
                  label="Allow guests to post"
                  checked={config.allowGuestsToPost}
                  onChange={(value) => handleConfigChange('allowGuestsToPost', value)}
                />
                
                <Checkbox
                  label="Moderate posts before publishing"
                  checked={config.moderateBeforePost}
                  onChange={(value) => handleConfigChange('moderateBeforePost', value)}
                />
              </>
            ) : (
              <>
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    checked={config.allowPersonalNotes}
                    onChange={(e) => handleConfigChange('allowPersonalNotes', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label className="ml-2 text-white">Allow personal notes</label>
                </div>
                
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    checked={config.showActivityFeed}
                    onChange={(e) => handleConfigChange('showActivityFeed', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label className="ml-2 text-white">Show activity feed</label>
                </div>
                
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    checked={config.allowGuestsToPost}
                    onChange={(e) => handleConfigChange('allowGuestsToPost', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label className="ml-2 text-white">Allow guests to post</label>
                </div>
                
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    checked={config.moderateBeforePost}
                    onChange={(e) => handleConfigChange('moderateBeforePost', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label className="ml-2 text-white">Moderate posts before publishing</label>
                </div>
              </>
            )}
          </div>
          
          <div className="space-y-3">
            {AppButton ? (
              <>
                <AppButton onClick={saveConfig} className="mb-2">
                  Save Configuration
                </AppButton>
                
                <AppButton 
                  onClick={resetToFactoryDefaults} 
                  className="bg-red-500/20 hover:bg-red-500/30 border-red-500/30"
                  disabled={isResetting}
                >
                  {isResetting ? 'Resetting...' : 'Reset to Factory Defaults'}
                </AppButton>
              </>
            ) : (
              <>
                <button
                  onClick={saveConfig}
                  className="w-full py-4 px-6 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-white text-center font-medium mb-2"
                >
                  Save Configuration
                </button>
                
                <button
                  onClick={resetToFactoryDefaults}
                  disabled={isResetting}
                  className="w-full py-4 px-6 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-xl text-white text-center font-medium"
                >
                  {isResetting ? 'Resetting...' : 'Reset to Factory Defaults'}
                </button>
              </>
            )}
          </div>
        </ContentPanel>
      ) : (
        <div className="bg-neutral-800/80 border border-white/10 rounded-2xl p-5">
          <h2 className="text-xl font-semibold text-white mb-2">Plugin Configuration</h2>
          <p className="text-sm text-white/70 mb-4">Configure how this plugin behaves for all users in this event.</p>
          
          {/* Configuration controls would go here */}
          
          <div className="mt-4 space-y-3">
            <button 
              onClick={saveConfig}
              className="w-full py-4 px-6 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-white text-center font-medium mb-2"
            >
              Save Configuration
            </button>
            
            <button
              onClick={resetToFactoryDefaults}
              disabled={isResetting}
              className="w-full py-4 px-6 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-xl text-white text-center font-medium"
            >
              {isResetting ? 'Resetting...' : 'Reset to Factory Defaults'}
            </button>
          </div>
        </div>
      )}
      
      <div className={styles.text.small + " mt-4"}>
        <p>User Role: Host Config View</p>
      </div>
    </div>
  );
}`,
}
